import * as React from "react";

import { cn } from "../../lib/utils";
import { AspectRatio } from "./aspect-ratio";
import { Card } from "./card";
import { Skeleton } from "./skeleton";
import { Text } from "./text";

/* -------------------------------------------------------------------------------------------------
 * Types
 * -----------------------------------------------------------------------------------------------*/

export type ContentCardDetailItem = {
	key: string;
	value: React.ReactNode;
};

/* -------------------------------------------------------------------------------------------------
 * ContentCardRoot
 * -----------------------------------------------------------------------------------------------*/

/**
 * Outer surface for a content card. Lays out a media header above a body region
 * using a two-row grid so the body fills the remaining height on equal-height grids.
 */
function ContentCardRoot({
	className,
	...props
}: React.ComponentProps<typeof Card>) {
	return (
		<Card
			data-slot="content-card"
			className={cn(
				"grid h-full grid-rows-[auto_1fr] overflow-hidden rounded-md border-muted shadow-none",
				className,
			)}
			{...props}
		/>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardMedia
 * -----------------------------------------------------------------------------------------------*/

export type ContentCardMediaProps = React.ComponentProps<"div"> & {
	/** Aspect ratio of the media region (width / height). Defaults to 5 / 3. */
	ratio?: number;
};

/**
 * Media header region. Frames its children (e.g. an image) at a fixed aspect ratio
 * and provides a background for cards without an image. Overlay slots such as
 * {@link ContentCardTags} and {@link ContentCardMediaFallback} compose inside it.
 */
function ContentCardMedia({
	className,
	ratio = 5 / 3,
	children,
	...props
}: ContentCardMediaProps) {
	return (
		<AspectRatio ratio={ratio}>
			<div
				data-slot="content-card-media"
				className={cn(
					"relative h-full w-full bg-primary-200 bg-cover bg-center bg-no-repeat p-3 lg:p-5",
					className,
				)}
				{...props}
			>
				{children}
			</div>
		</AspectRatio>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardTags
 * -----------------------------------------------------------------------------------------------*/

/**
 * Overlay wrapper for tags/badges, positioned above the media background.
 */
function ContentCardTags({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="content-card-tags"
			className={cn("relative z-10 [&>*]:shadow-xs", className)}
			{...props}
		/>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardMediaFallback
 * -----------------------------------------------------------------------------------------------*/

/**
 * Absolutely-centered slot used to display fallback content (e.g. an initials avatar)
 * when the card has no image.
 */
function ContentCardMediaFallback({
	className,
	...props
}: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="content-card-media-fallback"
			className={cn(
				"absolute left-0 top-0 flex h-full w-full items-center justify-center",
				className,
			)}
			{...props}
		/>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardBody
 * -----------------------------------------------------------------------------------------------*/

/**
 * Padded body region below the media. Lays its children out in a column with a
 * consistent gap so a footer can be pinned to the bottom via {@link ContentCardFooter}.
 */
function ContentCardBody({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="content-card-body"
			className={cn("flex h-full flex-col gap-y-4 self-start p-6", className)}
			{...props}
		/>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardContent
 * -----------------------------------------------------------------------------------------------*/

/**
 * Flexible content region inside {@link ContentCardBody} that grows to push the
 * footer to the bottom of the card.
 */
function ContentCardContent({
	className,
	...props
}: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="content-card-content"
			className={cn("flex-1", className)}
			{...props}
		/>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardTitle
 * -----------------------------------------------------------------------------------------------*/

function ContentCardTitle({
	className,
	...props
}: React.ComponentProps<typeof Text>) {
	return (
		<Text
			as="h3"
			variant="subheading-lg"
			data-slot="content-card-title"
			className={cn("mb-4 font-bold", className)}
			{...props}
		/>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardDetails
 * -----------------------------------------------------------------------------------------------*/

export type ContentCardDetailsProps = Omit<
	React.ComponentProps<"div">,
	"children"
> & {
	items: ContentCardDetailItem[];
};

/**
 * Divided list of key/value pairs, rendered as space-between rows separated by rules.
 */
function ContentCardDetails({
	items,
	className,
	...props
}: ContentCardDetailsProps) {
	if (!items.length) {
		return null;
	}

	return (
		<div
			data-slot="content-card-details"
			className={cn("mb-4 divide-y divide-muted", className)}
			{...props}
		>
			{items.map((detail) => (
				<div
					key={detail.key}
					className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 p-1.5"
				>
					<Text
						variant="body-default"
						className="min-w-0 break-words text-color-body-muted"
					>
						{detail.key}
					</Text>
					<Text
						variant="subheading-sm"
						className="min-w-0 break-words text-right"
					>
						{detail.value}
					</Text>
				</div>
			))}
		</div>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardDescription
 * -----------------------------------------------------------------------------------------------*/

/**
 * Wrapper for descriptive/blurb content. Accepts any `ReactNode` so consumers can
 * pass rendered markdown or plain text.
 */
function ContentCardDescription({
	className,
	...props
}: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="content-card-description"
			className={cn(className)}
			{...props}
		/>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardFooter
 * -----------------------------------------------------------------------------------------------*/

/**
 * Actions row pinned to the bottom of the body. Wraps its children so buttons keep
 * their labels and stack to full-width rows on narrow cards instead of overflowing.
 */
function ContentCardFooter({
	className,
	...props
}: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="content-card-footer"
			className={cn("flex flex-wrap gap-2", className)}
			{...props}
		/>
	);
}

/* -------------------------------------------------------------------------------------------------
 * ContentCardSkeleton
 * -----------------------------------------------------------------------------------------------*/

export type ContentCardSkeletonProps = React.ComponentProps<"div"> & {
	/** Aspect ratio of the media placeholder (width / height). Defaults to 5 / 3. */
	ratio?: number;
};

/**
 * Loading placeholder that mirrors the {@link ContentCard} layout: a media block
 * above a title and a few text lines.
 */
function ContentCardSkeleton({
	className,
	ratio = 5 / 3,
	...props
}: ContentCardSkeletonProps) {
	return (
		<Card
			data-slot="content-card-skeleton"
			className={cn(
				"overflow-hidden rounded-md border-muted shadow-none",
				className,
			)}
			{...props}
		>
			<AspectRatio ratio={ratio}>
				<Skeleton className="h-full w-full rounded-none" />
			</AspectRatio>
			<div className="p-6">
				<Skeleton className="h-6 w-32" />
				<div className="mt-4 space-y-2">
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-2/3" />
				</div>
			</div>
		</Card>
	);
}

/* -------------------------------------------------------------------------------------------------
 * Exports
 * -----------------------------------------------------------------------------------------------*/

const ContentCard = Object.assign(ContentCardRoot, {
	Media: ContentCardMedia,
	Tags: ContentCardTags,
	MediaFallback: ContentCardMediaFallback,
	Body: ContentCardBody,
	Content: ContentCardContent,
	Title: ContentCardTitle,
	Details: ContentCardDetails,
	Description: ContentCardDescription,
	Footer: ContentCardFooter,
	Skeleton: ContentCardSkeleton,
});

export {
	ContentCard,
	ContentCardRoot,
	ContentCardMedia,
	ContentCardTags,
	ContentCardMediaFallback,
	ContentCardBody,
	ContentCardContent,
	ContentCardTitle,
	ContentCardDetails,
	ContentCardDescription,
	ContentCardFooter,
	ContentCardSkeleton,
};
